import { Injectable, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import {
  map,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  startWith,
  catchError,
} from 'rxjs/operators';
import { Apollo } from 'apollo-angular';
import { gql } from 'apollo-angular';

import {
  ElementValidation,
  ValidationError,
} from '@workflow/models/element-configuration.interface';

/**
 * ELEMENT VALIDATION SERVICE
 *
 * PURPOSE: Provides real-time validation for workflow element configurations
 * combining Angular form validation with GraphQL server-side validation.
 *
 * RESPONSIBILITIES:
 * - Real-time form validation with debouncing to prevent excessive API calls
 * - Integration with GraphQL validation endpoints for business rule validation
 * - Combines client-side Angular form validation with server-side validation
 * - Provides validation status tracking (loading/idle states)
 * - Maps element types to their corresponding validation queries
 *
 * USAGE PATTERN:
 * - Element configuration components call formValueToFormErrorCount() to get real-time error count
 * - Service debounces form changes (300ms) to avoid excessive validation calls
 * - Returns combined count of Angular form errors + GraphQL validation errors
 * - Validation queries are type-specific (one per workflow element type)
 *
 * INTEGRATION POINTS:
 * - Used by all element configuration components (WebhookEventTrigger, DelayFlow, etc.)
 * - Connects to GraphQL validation endpoints on the backend
 * - Works with Angular Reactive Forms validation
 *
 * VALIDATION FLOW:
 * 1. Form value changes trigger validation (debounced)
 * 2. Angular form errors are counted immediately
 * 3. If no Angular errors, GraphQL validation is performed
 * 4. Combined error count is returned to component
 *
 * WHY SEPARATE: This is the ONLY service handling element validation to avoid
 * duplication and ensure consistent validation behavior across all elements.
 */
@Injectable({
  providedIn: 'root',
})
export class ElementValidationService {
  private apollo = inject(Apollo);

  private isValidatingSubject = new BehaviorSubject<boolean>(false);

  /**
   * Creates an observable that tracks form error count with real-time validation
   */
  formValueToFormErrorCount<T>(
    form: FormGroup,
    element: any,
    transformFunction: (formValue: any) => T,
  ): Observable<number> {
    return combineLatest([
      form.valueChanges.pipe(startWith(form.value), debounceTime(300), distinctUntilChanged()),
      form.statusChanges.pipe(startWith(form.status)),
    ]).pipe(
      switchMap(([formValue, status]) => {
        // Count Angular form validation errors
        const angularErrors = this.countFormErrors(form);

        // If form has basic validation errors, return immediately
        if (angularErrors > 0) {
          return new Observable<number>((subscriber) => {
            subscriber.next(angularErrors);
            subscriber.complete();
          });
        }

        // Perform GraphQL validation for complex business rules
        const elementInput = transformFunction(formValue);
        return this.validateElementConfiguration(element.id, elementInput, element.type).pipe(
          map((validation) => {
            const graphqlErrors = validation?.fieldExceptions?.length || 0;
            return angularErrors + graphqlErrors;
          }),
          catchError(() => {
            // On validation error, just return Angular errors
            return new Observable<number>((subscriber) => {
              subscriber.next(angularErrors);
              subscriber.complete();
            });
          }),
        );
      }),
    );
  }

  /**
   * Returns observable indicating if validation is in progress
   */
  isValidating(): Observable<boolean> {
    return this.isValidatingSubject.asObservable();
  }

  /**
   * Gets validation status observable for components to bind to
   * This is the standard method components should use
   */
  getValidationStatus(): Observable<boolean> {
    return this.isValidating();
  }

  /**
   * Gets form error count observable for components to bind to
   * This is a convenience method for consistent naming
   */
  getFormErrorCount(): Observable<number> {
    // Note: This method would typically be called with specific form parameters
    // It's provided here for interface consistency
    return new Observable<number>((subscriber) => {
      subscriber.next(0);
      subscriber.complete();
    });
  }

  /**
   * Validates element configuration using GraphQL
   */
  private validateElementConfiguration(
    elementId: string,
    elementInput: any,
    elementType: string,
  ): Observable<ElementValidation> {
    this.isValidatingSubject.next(true);

    // Create validation query based on element type
    const validationQuery = this.getValidationQuery(elementType);

    if (!validationQuery) {
      this.isValidatingSubject.next(false);
      return new Observable<ElementValidation>((subscriber) => {
        subscriber.next({ isValid: true, fieldExceptions: [] });
        subscriber.complete();
      });
    }

    return this.apollo
      .query({
        query: validationQuery,
        variables: {
          id: parseInt(elementId, 10),
          data: elementInput,
        },
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      })
      .pipe(
        map((result: any) => {
          this.isValidatingSubject.next(false);
          const validationKey = this.getValidationResultKey(elementType);
          return result.data?.[validationKey] || { isValid: true, fieldExceptions: [] };
        }),
        catchError((error) => {
          this.isValidatingSubject.next(false);
          console.error('Validation error:', error);
          return new Observable<ElementValidation>((subscriber) => {
            subscriber.next({ isValid: false, fieldExceptions: [] });
            subscriber.complete();
          });
        }),
      );
  }

  /**
   * Counts validation errors in Angular form
   */
  private countFormErrors(form: FormGroup): number {
    let errorCount = 0;

    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);
      if (control?.errors) {
        errorCount += Object.keys(control.errors).length;
      }

      // Recursively count errors in nested form groups
      if (control instanceof FormGroup) {
        errorCount += this.countFormErrors(control);
      }
    });

    return errorCount;
  }

  /**
   * Gets the appropriate validation query for element type
   */
  private getValidationQuery(elementType: string) {
    const validationQueries: { [key: string]: any } = {
      WebhookEventTriggerWorkflowElement: WEBHOOK_EVENT_VALIDATION_QUERY,
      WebhookActionWorkflowElement: WEBHOOK_ACTION_VALIDATION_QUERY,
      CreateObjectActionWorkflowElement: CREATE_OBJECT_VALIDATION_QUERY,
      UpdateObjectActionWorkflowElement: UPDATE_OBJECT_VALIDATION_QUERY,
      DeleteObjectActionWorkflowElement: DELETE_OBJECT_VALIDATION_QUERY,
      DelayFlowWorkflowElement: DELAY_FLOW_VALIDATION_QUERY,
      LocalChoiceFlowWorkflowElement: LOCAL_CHOICE_VALIDATION_QUERY,
      SendMessageActionWorkflowElement: SEND_MESSAGE_VALIDATION_QUERY,
      SendEmailActionWorkflowElement: SEND_EMAIL_VALIDATION_QUERY,
      GetOneObjectActionWorkflowElement: GET_ONE_OBJECT_VALIDATION_QUERY,
      GetObjectListActionWorkflowElement: GET_OBJECT_LIST_VALIDATION_QUERY,
      QueryObjectExistsActionWorkflowElement: QUERY_OBJECT_EXISTS_VALIDATION_QUERY,
      ObjectCreatedEventTriggerWorkflowElement: OBJECT_CREATED_EVENT_VALIDATION_QUERY,
      ObjectUpdatedEventTriggerWorkflowElement: OBJECT_UPDATED_EVENT_VALIDATION_QUERY,
      ObjectDeletedEventTriggerWorkflowElement: OBJECT_DELETED_EVENT_VALIDATION_QUERY,
      TimeEventTriggerWorkflowElement: TIME_EVENT_VALIDATION_QUERY,
      MessageEventTriggerWorkflowElement: MESSAGE_EVENT_VALIDATION_QUERY,
      MeetingEventTriggerWorkflowElement: MEETING_EVENT_VALIDATION_QUERY,
      WaitForMessageEventFlowWorkflowElement: WAIT_FOR_MESSAGE_EVENT_VALIDATION_QUERY,
    };

    return validationQueries[elementType];
  }

  /**
   * Gets the validation result key for element type
   */
  private getValidationResultKey(elementType: string): string {
    const resultKeys: { [key: string]: string } = {
      WebhookEventTriggerWorkflowElement: 'webhookEventTriggerValidation',
      WebhookActionWorkflowElement: 'webhookActionValidation',
      CreateObjectActionWorkflowElement: 'createObjectActionValidation',
      UpdateObjectActionWorkflowElement: 'updateObjectActionValidation',
      DeleteObjectActionWorkflowElement: 'deleteObjectActionValidation',
      DelayFlowWorkflowElement: 'delayFlowValidation',
      LocalChoiceFlowWorkflowElement: 'localChoiceFlowValidation',
      SendMessageActionWorkflowElement: 'sendMessageActionValidation',
      SendEmailActionWorkflowElement: 'sendEmailActionValidation',
      GetOneObjectActionWorkflowElement: 'getOneObjectActionValidation',
      GetObjectListActionWorkflowElement: 'getObjectListActionValidation',
      QueryObjectExistsActionWorkflowElement: 'queryObjectExistsActionValidation',
      ObjectCreatedEventTriggerWorkflowElement: 'objectCreatedEventTriggerValidation',
      ObjectUpdatedEventTriggerWorkflowElement: 'objectUpdatedEventTriggerValidation',
      ObjectDeletedEventTriggerWorkflowElement: 'objectDeletedEventTriggerValidation',
      TimeEventTriggerWorkflowElement: 'timeEventTriggerValidation',
      MessageEventTriggerWorkflowElement: 'messageEventTriggerValidation',
      MeetingEventTriggerWorkflowElement: 'meetingEventTriggerValidation',
      WaitForMessageEventFlowWorkflowElement: 'waitForMessageEventFlowValidation',
    };

    return resultKeys[elementType] || 'validation';
  }
}

// GraphQL Validation Queries
const WEBHOOK_EVENT_VALIDATION_QUERY = gql`
  query webhookEventTriggerValidation(
    $data: AutomatorWebhookEventTriggerWorkflowElementInput!
    $id: Long!
  ) {
    webhookEventTriggerValidation: validateAutomatorWebhookEventTriggerWorkflowElement(
      data: $data
      elementId: $id
    ) {
      isValid
      fieldExceptions {
        message
        propertyPath
        rejectedValue
      }
    }
  }
`;

const WEBHOOK_ACTION_VALIDATION_QUERY = gql`
  query webhookActionValidation($data: AutomatorWebhookActionWorkflowElementInput!, $id: Long!) {
    webhookActionValidation: validateAutomatorWebhookActionWorkflowElement(
      data: $data
      elementId: $id
    ) {
      isValid
      fieldExceptions {
        message
        propertyPath
        rejectedValue
      }
    }
  }
`;

const CREATE_OBJECT_VALIDATION_QUERY = gql`
  query createObjectActionValidation(
    $data: AutomatorCreateObjectActionWorkflowElementInput!
    $id: Long!
  ) {
    createObjectActionValidation: validateAutomatorCreateObjectActionWorkflowElement(
      data: $data
      elementId: $id
    ) {
      isValid
      fieldExceptions {
        message
        propertyPath
        rejectedValue
      }
    }
  }
`;

const UPDATE_OBJECT_VALIDATION_QUERY = gql`
  query updateObjectActionValidation(
    $data: AutomatorUpdateObjectActionWorkflowElementInput!
    $id: Long!
  ) {
    updateObjectActionValidation: validateAutomatorUpdateObjectActionWorkflowElement(
      data: $data
      elementId: $id
    ) {
      isValid
      fieldExceptions {
        message
        propertyPath
        rejectedValue
      }
    }
  }
`;

const DELETE_OBJECT_VALIDATION_QUERY = gql`
  query deleteObjectActionValidation(
    $data: AutomatorDeleteObjectActionWorkflowElementInput!
    $id: Long!
  ) {
    deleteObjectActionValidation: validateAutomatorDeleteObjectActionWorkflowElement(
      data: $data
      elementId: $id
    ) {
      isValid
      fieldExceptions {
        message
        propertyPath
        rejectedValue
      }
    }
  }
`;

const DELAY_FLOW_VALIDATION_QUERY = gql`
  query delayFlowValidation($data: AutomatorDelayFlowWorkflowElementInput!, $id: Long!) {
    delayFlowValidation: validateAutomatorDelayFlowWorkflowElement(data: $data, elementId: $id) {
      isValid
      fieldExceptions {
        message
        propertyPath
        rejectedValue
      }
    }
  }
`;

const LOCAL_CHOICE_VALIDATION_QUERY = gql`
  query localChoiceFlowValidation(
    $data: AutomatorLocalChoiceFlowWorkflowElementInput!
    $id: Long!
  ) {
    localChoiceFlowValidation: validateAutomatorLocalChoiceFlowWorkflowElement(
      data: $data
      elementId: $id
    ) {
      isValid
      fieldExceptions {
        message
        propertyPath
        rejectedValue
      }
    }
  }
`;

const SEND_MESSAGE_VALIDATION_QUERY = gql`
  query sendMessageActionValidation(
    $data: AutomatorSendMessageActionWorkflowElementInput!
    $id: Long!
  ) {
    sendMessageActionValidation: validateAutomatorSendMessageActionWorkflowElement(
      data: $data
      elementId: $id
    ) {
      isValid
      fieldExceptions {
        message
        propertyPath
        rejectedValue
      }
    }
  }
`;

const SEND_EMAIL_VALIDATION_QUERY = gql`
  query sendEmailActionValidation(
    $data: AutomatorSendEmailActionWorkflowElementInput!
    $id: Long!
  ) {
    sendEmailActionValidation: validateAutomatorSendEmailActionWorkflowElement(
      data: $data
      elementId: $id
    ) {
      isValid
      fieldExceptions {
        message
        propertyPath
        rejectedValue
      }
    }
  }
`;

const GET_ONE_OBJECT_VALIDATION_QUERY = gql`
  query getOneObjectActionValidation(
    $data: AutomatorGetOneObjectActionWorkflowElementInput!
    $id: Long!
  ) {
    getOneObjectActionValidation: validateAutomatorGetOneObjectActionWorkflowElement(
      data: $data
      elementId: $id
    ) {
      isValid
      fieldExceptions {
        message
        propertyPath
        rejectedValue
      }
    }
  }
`;

const GET_OBJECT_LIST_VALIDATION_QUERY = gql`
  query getObjectListActionValidation(
    $data: AutomatorGetObjectListActionWorkflowElementInput!
    $id: Long!
  ) {
    getObjectListActionValidation: validateAutomatorGetObjectListActionWorkflowElement(
      data: $data
      elementId: $id
    ) {
      isValid
      fieldExceptions {
        message
        propertyPath
        rejectedValue
      }
    }
  }
`;

const QUERY_OBJECT_EXISTS_VALIDATION_QUERY = gql`
  query queryObjectExistsActionValidation(
    $data: AutomatorQueryObjectExistsActionWorkflowElementInput!
    $id: Long!
  ) {
    queryObjectExistsActionValidation: validateAutomatorQueryObjectExistsActionWorkflowElement(
      data: $data
      elementId: $id
    ) {
      isValid
      fieldExceptions {
        message
        propertyPath
        rejectedValue
      }
    }
  }
`;

const OBJECT_CREATED_EVENT_VALIDATION_QUERY = gql`
  query objectCreatedEventTriggerValidation(
    $data: AutomatorObjectCreatedEventTriggerWorkflowElementInput!
    $id: Long!
  ) {
    objectCreatedEventTriggerValidation: validateAutomatorObjectCreatedEventTriggerWorkflowElement(
      data: $data
      elementId: $id
    ) {
      isValid
      fieldExceptions {
        message
        propertyPath
        rejectedValue
      }
    }
  }
`;

const OBJECT_UPDATED_EVENT_VALIDATION_QUERY = gql`
  query objectUpdatedEventTriggerValidation(
    $data: AutomatorObjectUpdatedEventTriggerWorkflowElementInput!
    $id: Long!
  ) {
    objectUpdatedEventTriggerValidation: validateAutomatorObjectUpdatedEventTriggerWorkflowElement(
      data: $data
      elementId: $id
    ) {
      isValid
      fieldExceptions {
        message
        propertyPath
        rejectedValue
      }
    }
  }
`;

const OBJECT_DELETED_EVENT_VALIDATION_QUERY = gql`
  query objectDeletedEventTriggerValidation(
    $data: AutomatorObjectDeletedEventTriggerWorkflowElementInput!
    $id: Long!
  ) {
    objectDeletedEventTriggerValidation: validateAutomatorObjectDeletedEventTriggerWorkflowElement(
      data: $data
      elementId: $id
    ) {
      isValid
      fieldExceptions {
        message
        propertyPath
        rejectedValue
      }
    }
  }
`;

const TIME_EVENT_VALIDATION_QUERY = gql`
  query timeEventTriggerValidation(
    $data: AutomatorTimeEventTriggerWorkflowElementInput!
    $id: Long!
  ) {
    timeEventTriggerValidation: validateAutomatorTimeEventTriggerWorkflowElement(
      data: $data
      elementId: $id
    ) {
      isValid
      fieldExceptions {
        message
        propertyPath
        rejectedValue
      }
    }
  }
`;

const MESSAGE_EVENT_VALIDATION_QUERY = gql`
  query messageEventTriggerValidation(
    $data: AutomatorMessageEventTriggerWorkflowElementInput!
    $id: Long!
  ) {
    messageEventTriggerValidation: validateAutomatorMessageEventTriggerWorkflowElement(
      data: $data
      elementId: $id
    ) {
      isValid
      fieldExceptions {
        message
        propertyPath
        rejectedValue
      }
    }
  }
`;

const MEETING_EVENT_VALIDATION_QUERY = gql`
  query meetingEventTriggerValidation(
    $data: AutomatorMeetingEventTriggerWorkflowElementInput!
    $id: Long!
  ) {
    meetingEventTriggerValidation: validateAutomatorMeetingEventTriggerWorkflowElement(
      data: $data
      elementId: $id
    ) {
      isValid
      fieldExceptions {
        message
        propertyPath
        rejectedValue
      }
    }
  }
`;

const WAIT_FOR_MESSAGE_EVENT_VALIDATION_QUERY = gql`
  query waitForMessageEventFlowValidation(
    $data: AutomatorWaitForMessageEventFlowWorkflowElementInput!
    $id: Long!
  ) {
    waitForMessageEventFlowValidation: validateAutomatorWaitForMessageEventFlowWorkflowElement(
      data: $data
      elementId: $id
    ) {
      isValid
      fieldExceptions {
        message
        propertyPath
        rejectedValue
      }
    }
  }
`;
